/**
 * Sanitizes sensitive data from log entries.
 *
 * This function recursively traverses objects and arrays to redact
 * sensitive information like passwords, tokens, API keys, etc.
 *
 * @param data - The data to sanitize
 * @param visited - WeakSet to track visited objects (for circular reference detection)
 * @returns Sanitized data with sensitive values replaced by "[REDACTED]"
 */
export function sanitize(data: any, visited = new WeakSet()): any {
  // Use explicit flag to bypass sanitization - only for local debugging
  // WARNING: Never enable DISABLE_LOG_SANITIZATION in production as it will log sensitive data in plain text
  if (process.env.DISABLE_LOG_SANITIZATION) {
    return data;
  }
  if (!data || typeof data !== "object") {
    // Handle BigInt and other non-serializable primitives
    if (typeof data === "bigint") {
      return data.toString();
    }
    if (typeof data === "function") {
      return "[FUNCTION]";
    }
    if (typeof data === "symbol") {
      return "[SYMBOL]";
    }
    return data;
  }

  // Handle circular references
  if (visited.has(data)) {
    return "[CIRCULAR]";
  }
  visited.add(data);

  const sensitiveKeys = [
    "password",
    "token",
    "accesstoken",
    "access_token",
    "refreshtoken",
    "refresh_token",
    "idtoken",
    "id_token",
    "apikey",
    "api_key",
    "private_key",
    "secret",
    "authorization",
    "bearer",
    "jwt",
    "credentials",
    "cookie",
    "session",
    "creditcard",
    "credit_card",
    "cardnumber",
    "card_number",
    "cvv",
    "cvc",
    "pin",
    "otp",
    "ssn",
  ];

  const sanitized = Array.isArray(data) ? [...data] : { ...data };

  if (Array.isArray(sanitized)) {
    return sanitized.map((item) => sanitize(item, visited));
  }

  Object.keys(sanitized).forEach((key) => {
    // Normalize key for comparison (remove special characters, convert to lowercase)
    const normalizedKey = key.toLowerCase().replace(/[-._]/g, "");

    // Check if normalized key contains sensitive terms
    if (
      sensitiveKeys.some((sensitive) =>
        normalizedKey.includes(sensitive.replace(/_/g, "")),
      )
    ) {
      sanitized[key] = "[REDACTED]";
    } else if (typeof sanitized[key] === "object" && sanitized[key] !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitize(sanitized[key], visited);
    } else {
      // Handle primitive values that might not be JSON serializable
      sanitized[key] = sanitize(sanitized[key], visited);
    }
  });

  return sanitized;
}
